/* ═══════════════════════════════════════════════════════════════
   The Designer — live CSI numbering and paragraph layout.

   Numbers are DECORATIONS, never document content. Nobody can type,
   delete or edit a paragraph number: it is computed from the level
   sequence by the ONE body-layout brain (shared/body-layout.ts), the
   same module that tells word-engine what to print. If the paper
   shows "1.03", the .docx says "1.03".

   The same plugin styles each paragraph from its level — indent,
   hanging, spacing, alignment, emphasis — so the paper the caret sits
   on IS the formatted output, not an approximation of it.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import {
  bodyParagraphCss,
  computeNumbering,
  numberColumnPx,
  resolveBodyLayout,
  type BodyLevelLayout,
} from '../../shared/body-layout';
import { TABLE_NODE_NAME } from './tables';

export const numberingPluginKey = new PluginKey('designerNumbering');

/** Ask the plugin to rebuild after something outside the document changed
 *  (the forma was edited, a different forma was picked). */
export const REBUILD_LAYOUT = 'designer:rebuildLayout';

export interface NumberingOptions {
  /** Read the forma at decoration time — it can change under the editor. */
  getForma: () => StandardConfig | null;
}

/** The block-shaped view of a node the numbering brain needs. A table only
 *  consumes a counter/prefix when it opted in (`numbered: true`, §4 R7) —
 *  standalone tables (the default) sit at the margin between paragraphs and
 *  must not shift the numbering of what follows them. */
function nodeAsBlock(node: PMNode): ContentBlock {
  const isTable = node.type.name === TABLE_NODE_NAME;
  return {
    id: (node.attrs.blockId as string) || '',
    level: (node.attrs.level as number) || 1,
    text: '',
    continuation: !!node.attrs.continuation,
    excluded: !!node.attrs.excluded,
    ...(isTable
      ? { blockType: 'table' as const, tableData: { numbered: !!node.attrs.numbered } as any }
      : {}),
  };
}

function cssText(css: Record<string, string | number | undefined>): string {
  return Object.entries(css)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}:${v}`)
    .join(';');
}

function numberWidget(prefix: string, layout: BodyLevelLayout): HTMLElement {
  const span = document.createElement('span');
  span.className = 'dsg-num';
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('aria-hidden', 'true');
  span.textContent = prefix;
  // Word tabs from the number to the wrap edge; the hanging indent IS that
  // tab stop, so the number column is exactly that wide.
  span.style.minWidth = `${numberColumnPx(layout)}px`;
  return span;
}

function buildDecorations(state: EditorState, forma: StandardConfig | null): DecorationSet {
  const layouts = resolveBodyLayout(forma);
  const blocks: ContentBlock[] = [];
  const positions: number[] = [];
  const nodes: PMNode[] = [];

  state.doc.forEach((node, offset) => {
    blocks.push(nodeAsBlock(node));
    positions.push(offset);
    nodes.push(node);
  });

  const refs = computeNumbering(blocks, forma);
  const decorations: Decoration[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const pos = positions[i];
    const level = Math.max(1, Math.min(9, (node.attrs.level as number) || 1));
    let layout = layouts[level - 1];
    const prevLevel = i > 0 ? (nodes[i - 1].attrs.level as number) : null;
    const nextLevel = i < nodes.length - 1 ? (nodes[i + 1].attrs.level as number) : null;

    // §4 R7: a STANDALONE table (the default) sits at the margin between
    // paragraphs — no number, no indent. A NUMBERED table takes the level's
    // indent exactly like a numbered paragraph does.
    const isTable = node.type.name === TABLE_NODE_NAME;
    const numberedTable = isTable && !!node.attrs.numbered;
    if (isTable && !numberedTable) {
      layout = { ...layout, leftTwips: 0, hangingTwips: 0, firstLineTwips: 0 };
    }

    const css = bodyParagraphCss(layout, {
      sameStyleAsPrev: prevLevel === level,
      sameStyleAsNext: nextLevel === level,
      continuation: !!node.attrs.continuation,
    });

    const classes = ['dsg-block', `dsg-level-${level}`];
    if (node.attrs.excluded) classes.push('dsg-excluded');
    if (node.attrs.continuation) classes.push('dsg-continuation');
    if (isTable) classes.push(numberedTable ? 'dsg-table-numbered' : 'dsg-table-standalone');

    decorations.push(
      Decoration.node(pos, pos + node.nodeSize, {
        class: classes.join(' '),
        style: cssText({ ...css }),
        'data-block-id': (node.attrs.blockId as string) || '',
        'data-level': String(level),
      })
    );

    const prefix = refs[i]?.prefix || '';
    // A paragraph's widget sits INSIDE it, at the text start (pos + 1). A
    // numbered table's widget sits just BEFORE it (pos) — the number reads as
    // the table's anchor, the way the .docx anchor paragraph works.
    if (prefix && node.type.name === 'specParagraph') {
      decorations.push(
        Decoration.widget(pos + 1, () => numberWidget(prefix, layout), {
          side: -1,
          key: `num-${node.attrs.blockId}-${prefix}-${level}`,
        })
      );
    } else if (prefix && numberedTable) {
      decorations.push(
        Decoration.widget(pos, () => numberWidget(prefix, layout), {
          side: -1,
          key: `num-${node.attrs.blockId}-${prefix}-${level}`,
        })
      );
    }
  }

  return DecorationSet.create(state.doc, decorations);
}

/**
 * Live numbering + level styling. Rebuilds only when the document changes or
 * a REBUILD_LAYOUT transaction arrives, so typing inside one paragraph does
 * not re-derive the whole document's layout on every keystroke.
 */
export const Numbering = Extension.create<NumberingOptions>({
  name: 'designerNumbering',

  addOptions() {
    return { getForma: () => null };
  },

  addProseMirrorPlugins() {
    const getForma = () => this.options.getForma();
    return [
      new Plugin({
        key: numberingPluginKey,
        state: {
          init: (_config, state) => buildDecorations(state, getForma()),
          apply: (tr: Transaction, value: DecorationSet, _old, newState) => {
            if (tr.docChanged || tr.getMeta(REBUILD_LAYOUT)) {
              return buildDecorations(newState, getForma());
            }
            return value.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return numberingPluginKey.getState(state) as DecorationSet;
          },
        },
      }),
    ];
  },
});
